import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import {
  ExternalExportStatus,
  VetaCenterReadinessRecordType,
  VetaCenterReadinessSeverity,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { NOT_FOUND } from '~constants/errorMessagesConsts';
import {
  VetaCenterConnection,
  VetaCenterProjectLink,
  VetaCenterReadinessItem,
  VetaCenterTestTicket,
} from '~hooks/useVetaCenter/models';
import useVetaCenter from '~hooks/useVetaCenter/useVetaCenter';
import useVetaCenterPollWindow from '~hooks/useVetaCenter/useVetaCenterPollWindow';
import { VETA_CENTER_DATE_TIME_FORMAT } from '~hooks/useVetaCenter/vetaCenterConstants';
import { getGapLabel } from '~hooks/useVetaCenter/vetaCenterReadinessRecords';
import useVetaCenterReadinessItemsQuery from '~queries/useVetaCenterReadinessItemsQuery';
import useVetaCenterTestTicketsInfiniteQuery, {
  getVetaCenterTestTicketsQueryKey,
} from '~queries/useVetaCenterTestTicketsInfiniteQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import isEnumValue from '~utils/isEnumValue';

const KEY_PREFIX = 'administration.integration.veta_center.test_tickets';

const statusChips = {
  [ExternalExportStatus.EXPORTED]: {
    color: 'success',
    labelKey: `${KEY_PREFIX}.statuses.exported`,
  },
  [ExternalExportStatus.NEEDS_ATTENTION]: {
    color: 'error',
    labelKey: `${KEY_PREFIX}.statuses.needs_attention`,
  },
  [ExternalExportStatus.NOT_APPLICABLE]: {
    color: 'default',
    labelKey: `${KEY_PREFIX}.statuses.not_applicable`,
  },
  [ExternalExportStatus.PENDING]: {
    color: 'info',
    labelKey: `${KEY_PREFIX}.statuses.pending`,
  },
  [ExternalExportStatus.UNKNOWN_OUTCOME]: {
    color: 'warning',
    labelKey: `${KEY_PREFIX}.statuses.unknown_outcome`,
  },
} satisfies Record<
  ExternalExportStatus,
  { color: 'default' | 'error' | 'info' | 'success' | 'warning'; labelKey: string }
>;

const unknownStatusChip = {
  color: 'default',
  labelKey: `${KEY_PREFIX}.statuses.unknown`,
} as const;

interface VetaCenterTestTicketsProps {
  companyId: string;
  projectLinks: VetaCenterProjectLink[];
  vetaCenterConnection: VetaCenterConnection;
}

export default function VetaCenterTestTickets({
  companyId,
  projectLinks,
  vetaCenterConnection,
}: VetaCenterTestTicketsProps) {
  const { toasterStore } = useStores();
  const queryClient = useQueryClient();
  const { sendTestTicket } = useVetaCenter();
  const { isPolling, startPolling, stopPolling } = useVetaCenterPollWindow();
  const {
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
    testTickets,
  } = useVetaCenterTestTicketsInfiniteQuery(
    companyId,
    projectLinks.map((link) => link.id),
    isPolling,
  );
  const {
    isError: isReadinessError,
    isLoading: isReadinessLoading,
    items,
  } = useVetaCenterReadinessItemsQuery(companyId, {
    isEnabled: projectLinks.length > 0,
  });
  const [projectLinkIdBySiteId, setProjectLinkIdBySiteId] = useState<
    Record<string, string>
  >({});
  const [sendError, setSendError] = useState<string | null>(null);
  const [siteToSend, setSiteToSend] = useState<VetaCenterTestTicket | null>(null);
  const [isSending, setIsSending] = useState(false);
  const isMutatingRef = useRef(false);

  const hasPendingTestTicket = testTickets.some(
    (testTicket) => testTicket.status === ExternalExportStatus.PENDING,
  );

  useEffect(() => {
    if (hasPendingTestTicket) startPolling();
    else stopPolling();
  }, [hasPendingTestTicket, startPolling, stopPolling]);

  const contractLabelByGuid = new Map(
    vetaCenterConnection.contracts.map((contract) => [
      contract.contractGuid,
      contract.contractId ?? contract.contractGuid,
    ]),
  );
  const getLinkLabel = (link: VetaCenterProjectLink) =>
    `${contractLabelByGuid.get(link.externalContractGuid) ?? link.externalContractGuid} · ${link.project.name}`;

  const firstBlockingGapBySiteId = new Map<string, VetaCenterReadinessItem>();
  items.forEach((item) => {
    const isBlockingSiteGap =
      item.recordType === VetaCenterReadinessRecordType.SITE &&
      item.severity === VetaCenterReadinessSeverity.BLOCK;
    if (isBlockingSiteGap && !firstBlockingGapBySiteId.has(item.recordId)) {
      firstBlockingGapBySiteId.set(item.recordId, item);
    }
  });

  const getSelectedLinkId = (siteId: string) => {
    const selectedLinkId = projectLinkIdBySiteId[siteId];

    return projectLinks.some((link) => link.id === selectedLinkId) ? selectedLinkId : '';
  };

  const hasLinkChoice = projectLinks.length > 1;
  const selectedLinkIdToSend = hasLinkChoice
    ? getSelectedLinkId(siteToSend?.siteId ?? '')
    : '';
  const linkToSend = hasLinkChoice
    ? projectLinks.find((link) => link.id === selectedLinkIdToSend)
    : projectLinks[0];
  const projectLinkIdToSend = selectedLinkIdToSend || undefined;

  const handleConfirm = async () => {
    if (!siteToSend || isMutatingRef.current) return;
    isMutatingRef.current = true;

    setIsSending(true);
    setSendError(null);
    try {
      const isSent = await sendTestTicket(companyId, {
        projectLinkId: projectLinkIdToSend,
        siteId: siteToSend.siteId,
      });
      if (!isSent) return;

      setSiteToSend(null);
      startPolling();
      toasterStore.push(alert(t(`${KEY_PREFIX}.toast_sent`), AlertTypes.success));
      await queryClient.invalidateQueries({
        queryKey: getVetaCenterTestTicketsQueryKey(companyId),
      });
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== NOT_FOUND) throw error;

      setSendError(
        t(
          projectLinks.length > 1
            ? `${KEY_PREFIX}.not_pickup_site_of_link`
            : `${KEY_PREFIX}.not_pickup_site`,
        ),
      );
    } finally {
      isMutatingRef.current = false;
      setIsSending(false);
    }
  };

  const renderRow = (testTicket: VetaCenterTestTicket) => {
    const chip = isEnumValue(ExternalExportStatus, testTicket.status)
      ? statusChips[testTicket.status]
      : unknownStatusChip;
    const gap = firstBlockingGapBySiteId.get(testTicket.siteId);
    const isAwaitingReadiness = isReadinessLoading || isReadinessError;
    const isSendBlocked =
      projectLinks.length === 0 ||
      isAwaitingReadiness ||
      Boolean(gap) ||
      isSending ||
      testTicket.status === ExternalExportStatus.PENDING;

    return (
      <Box
        key={testTicket.siteId}
        sx={{
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ minWidth: 180 }}>
          <Typography sx={{ fontWeight: 600 }}>{testTicket.siteName}</Typography>
        </Box>

        {projectLinks.length > 1 ? (
          <TextField
            label={t(`${KEY_PREFIX}.link_label`)}
            onChange={(event) =>
              setProjectLinkIdBySiteId((current) => ({
                ...current,
                [testTicket.siteId]: event.target.value,
              }))
            }
            select
            size="small"
            sx={{ minWidth: 260 }}
            value={getSelectedLinkId(testTicket.siteId)}
          >
            <MenuItem value="">{t(`${KEY_PREFIX}.oldest_link`)}</MenuItem>
            {projectLinks.map((link) => (
              <MenuItem key={link.id} value={link.id}>
                {getLinkLabel(link)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}

        <Chip color={chip.color} label={t(chip.labelKey)} size="small" />
        {testTicket.sentAt ? (
          <Typography sx={{ color: 'text.secondary' }}>
            {dayjs.tz(testTicket.sentAt).format(VETA_CENTER_DATE_TIME_FORMAT)}
          </Typography>
        ) : null}
        {testTicket.reason ? (
          <Typography sx={{ color: 'error.main' }}>{testTicket.reason}</Typography>
        ) : null}

        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
          <Tooltip
            title={
              gap
                ? t(`${KEY_PREFIX}.fix_first`, {
                    gap: getGapLabel(gap.kind),
                    interpolation: { escapeValue: false },
                  })
                : ''
            }
          >
            <span>
              <SmallLoadingButton
                color="brandV2Black"
                disabled={isSendBlocked}
                onClick={() => setSiteToSend(testTicket)}
                sx={{ borderColor: 'divider', color: 'text.primary' }}
                variant="outlined"
              >
                {testTicket.status === ExternalExportStatus.NOT_APPLICABLE
                  ? t(`${KEY_PREFIX}.send`)
                  : t(`${KEY_PREFIX}.resend`)}
              </SmallLoadingButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const renderList = () => {
    if (projectLinks.length === 0) return null;

    if (isError) {
      return (
        <Alert
          action={
            <SmallButton color="secondary" onClick={() => refetch()}>
              {t('actions.retry')}
            </SmallButton>
          }
          severity="error"
        >
          {t('error_messages.veta_center.failed_to_fetch_test_tickets')}
        </Alert>
      );
    }

    if (isLoading) return <CircularProgress size={24} />;

    if (testTickets.length === 0) {
      return <Typography>{t(`${KEY_PREFIX}.none`)}</Typography>;
    }

    return testTickets.map(renderRow);
  };

  const handleRefreshClick = () => {
    startPolling();
    refetch();
  };

  const handleDialogClose = () => {
    if (isSending) return;

    setSiteToSend(null);
    setSendError(null);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
        <Typography variant="h6">{t(`${KEY_PREFIX}.title`)}</Typography>
        <SmallLoadingButton
          color="brandV2Black"
          disabled={projectLinks.length === 0}
          loading={isFetching}
          onClick={handleRefreshClick}
          sx={{ borderColor: 'divider', color: 'text.primary' }}
          variant="outlined"
        >
          {t(`${KEY_PREFIX}.refresh`)}
        </SmallLoadingButton>
      </Box>
      <Typography sx={{ color: 'text.secondary', my: 1 }}>
        {t(`${KEY_PREFIX}.description`)}
      </Typography>
      {renderList()}
      {hasNextPage ? (
        <SmallLoadingButton
          color="brandV2Black"
          loading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          sx={{ borderColor: 'divider', color: 'text.primary', mt: 1 }}
          variant="outlined"
        >
          {t(`${KEY_PREFIX}.load_more`)}
        </SmallLoadingButton>
      ) : null}

      <Dialog isOpen={Boolean(siteToSend)} maxWidth="sm" onClose={handleDialogClose}>
        <DialogHeader onClose={handleDialogClose}>
          {t(`${KEY_PREFIX}.confirmation_title`)}
        </DialogHeader>
        <DialogContent>
          <Typography>
            {t(`${KEY_PREFIX}.confirmation_body`, {
              contract: linkToSend
                ? getLinkLabel(linkToSend)
                : t(`${KEY_PREFIX}.oldest_link`),
              interpolation: { escapeValue: false },
              plant: siteToSend?.siteName,
            })}
          </Typography>
          {sendError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {sendError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isSending}
            onClick={handleDialogClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>
          <SmallLoadingButton
            color="brandV2Black"
            loading={isSending}
            onClick={handleConfirm}
            variant="contained"
          >
            {t(`${KEY_PREFIX}.send`)}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
